/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  InputNumber,
  TimePicker,
} from 'antd';
import {
  CheckCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';
import {
  ModelRegistroGuardiasTable,
} from 'api/configuraciones/ConfigurarGuardiasVacacionales/RegistroGuardias/RegistroGuardiasTable';
import moment from 'moment';
import { getGuardiasVacacionalesPeriodos } from 'api/configuraciones/ConfigurarGuardiasVacacionales/ConfigurarGuardiasVacacionalesPeriodos';
import { getGuardiasVacacionalesGeneral } from 'api/configuraciones/ConfigurarGuardiasVacacionales/ConfigurarGuardiasVacacionalesGeneral';
import { buildDateRange, ModelRegistroGuardiasFechas, numeroDiasName } from 'api/configuraciones/ConfigurarGuardiasVacacionales/RegistroGuardias/RegistroGuardiasFechas';
import { getModelPermission } from 'utils/permission';
import RegistroGuardiasFechas from './RegistroGuardiasFechas';

const modelPermissionName = 'registrodiasguardia';

export const registroGuardiasTablePermission = {
  permissionModel: modelPermissionName,
  catalogsModels: [],
  tabsModels: [],
};
/**
 * @typedef {Object} FatherData
 * @property {number} id        // primary key
 * @property {number} antiguedad_numerico_anos
 * @property {number} antiguedad_numerico_dias
 * @property {string} antiguedad_texto
 * @property {number} jornada_laboral
 * @property {moment.Moment} de_las
 * @property {moment.Moment} a_las
 */
/**
 * @typedef {Object} MyComponentProps
 * @property {FatherData} fatherData
 */
/**
 * @param {MyComponentProps} props
 */
const RegistroGuardiasTable = ({
  permission, fatherData, autoEditRecordId, hideTable, onHideTableClose,
}) => {
  const [form] = Form.useForm();
  const formInstances = [
    form,
  ];

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [PeriodosVacacionales, setPeriodosVacacionales] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [pageActual, setPageActual] = useState(1);
  const [open, setVisible] = useState(false);
  const [guardiasVacacionalGeneral, setGuardiasVacacionalGeneral] = useState([]);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [estados, setEstados] = useState([]);

  const [dates, setDates] = useState([]);

  const guardiasVacacionalGeneralName = 'configurar_guardias_vacacionales_general';
  const guardiasVacacionalGeneralWatcher = Form.useWatch(guardiasVacacionalGeneralName, form);
  useEffect(() => {
    const fetchAll = async () => {
      const _periodosVacacionales = await getGuardiasVacacionalesPeriodos({
        estados_globales: ESTATUS_AUTORIZADO,
        configurar_guardias_vacacionales_general: guardiasVacacionalGeneralWatcher,
      });
      setPeriodosVacacionales(_periodosVacacionales);
    };
    if (guardiasVacacionalGeneralWatcher !== undefined) {
      setDisableFields(true);
      fetchAll();
      setDisableFields(false);
    }
  }, [guardiasVacacionalGeneralWatcher]);

  const workingDaysName = 'permite_sabado_y_domingo';
  const periodoVacacionalName = 'configurar_guardias_vacacionales';
  const aplicaGuardias = 'aplica_horario_de_guardias';
  const workingDaysWatcher = Form.useWatch(workingDaysName, form);
  const periodoVacacionalWatcher = Form.useWatch(periodoVacacionalName, form);
  useEffect(() => {
    if (periodoVacacionalWatcher !== undefined) {
      setLoading(true);
      const periodoVacacional = PeriodosVacacionales?.find(
        (e) => e.id === periodoVacacionalWatcher,
      );
      const _fecha_inicial = moment(periodoVacacional?.fecha_inicial);
      const _fecha_final = moment(periodoVacacional?.fecha_final);
      form.setFieldsValue({
        fecha_inicial: _fecha_inicial,
        fecha_final: _fecha_final,
      });
      const rangeOfDates = buildDateRange(_fecha_inicial, _fecha_final, !workingDaysWatcher);
      const _dates = rangeOfDates.map((value) => ({ id: value.format('YYYY-MM-DD'), fecha: value }));
      setDates(_dates);
      setLoading(false);
    }
  }, [periodoVacacionalWatcher, workingDaysWatcher]);

  const registroName = `${selectedData?.configurar_guardias_vacacionales
    ?.periodos_vacacionales?.periodo}`;
  const {
    cliente,
    nameModel,
    nameModelSingular,
    formatter,
  } = ModelRegistroGuardiasTable;

  const fetchPaginated = async (page = 1, q = null) => {
    try {
      setLoading(true);
      const cleanPage = Number(String(page).replace(/\D/g, ''));
      const _data = await cliente.obtenerListaDeRegistros(
        {
          empleado: fatherData.id,
          page: cleanPage,
          ...q,
        },
      );
      const { results } = _data;

      setSelectedRowKeys([]);
      setPageActual(cleanPage);
      setData({ ..._data, results: [...results] });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const fetchData = async (_page = 1) => {
    setLoading(true);
    const _data = await cliente.obtenerListaDeRegistros({
      empleado: fatherData.id,
      page: _page,
    });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _guardiasVacacionalGeneral,
        _data,
        _estados,
      ] = await Promise.all([
        getGuardiasVacacionalesGeneral({ estados_globales: ESTATUS_AUTORIZADO }),
        cliente.obtenerListaDeRegistros({
          empleado: fatherData.id,
          page: 1,
        }),
        getEstadosGlobales(),
      ]);
      if (mounted) {
        setGuardiasVacacionalGeneral(_guardiasVacacionalGeneral);
        setData(_data);
        setEstados(_estados);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    setDates([]);
    if (hideTable && onHideTableClose) onHideTableClose();
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });

    const dataForForm = {
      ...formatter.formatRecordForRead(record),
      a_las: record?.a_las
        ? moment(record?.a_las, 'HH:mm:ss', true)
        : fatherData?.a_las,
      de_las: record?.de_las
        ? moment(record?.de_las, 'HH:mm:ss', true)
        : fatherData?.de_las,
    };

    form.setFieldsValue(dataForForm);
  };
  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const {
        fechas, // no mandar
        [workingDaysName]: _, // no mandar
        dias_guardia, // no mandar
        numero_dias, // no mandar
        ...valuesForm
      } = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const values = formatter.formatRecordForSend(valuesForm);
      const saved = await (
        key
          ? cliente.actualizarRegistro(key, values)
          : cliente.crearRegistro(values)
      );
      if (saved) {
        if (!key) {
          handleOnRowClick(saved);
        } else {
          onCancel();
        }
        await fetchData(pageActual);
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };
  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data?.results?.find((item) => item.id === key);
    const _regristo = await cliente.obtenerUnRegistro(match.id);
    setTimeout(() => {
      if (_regristo) {
        setSelectedRowKeys([_regristo.id]);
        setSelectedData({ ..._regristo });
        const formValues = {
          ...formatter.formatRecordForRead(_regristo),
          a_las: _regristo?.a_las
            ? moment(_regristo?.a_las, 'HH:mm:ss', true)
            : fatherData?.a_las,
          de_las: _regristo?.de_las
            ? moment(_regristo?.de_las, 'HH:mm:ss', true)
            : fatherData?.de_las,
        };
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(formValues));
          setDisableFields(_regristo?.estados_globales?.id !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  useEffect(() => {
    if (!autoEditRecordId) return;
    onClickEdit({ id: autoEditRecordId });
  }, []); // eslint-disable-line

  const onClickDelete = () => {
    setVisibleAlert(true);
  };
  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await cliente.borrarRegistro(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar registros ya autorizados');
        return;
      }
      await cliente.actualizarRegistro(key, { estados_globales: ESTATUS_CANCELADO });

      await fetchData(pageActual);
      onCancel();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };
  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, `No se puede autorizar ${nameModelSingular} ya autorizado`);
        return;
      }
      await cliente.autorizarRegistro(key);

      await fetchData(pageActual);
      onCancel();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmReactivar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        return;
      }
      await cliente.actualizarRegistro(key, { estados_globales: ESTATUS_ACTIVO });

      await fetchData(pageActual);
      onCancel();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Antigüedad',
      dataIndex: 'antiguedad_texto',
      key: 'antiguedad_texto',
      width: 100,
    },
    {
      titleText: 'Año de Guardias',
      dataIndex: 'configurar_guardias_vacacionales_general',
      key: 'configurar_guardias_vacacionales_general',
      width: 100,
      render: (_, record) => record?.configurar_guardias_vacacionales_general
        ?.periodo_fiscal?.periodo,
    },
    {
      titleText: 'Periodo Vacacional',
      dataIndex: 'configurar_guardias_vacacionales',
      key: 'configurar_guardias_vacacionales',
      width: 200,
      render: (_, record) => record?.configurar_guardias_vacacionales
        ?.periodos_vacacionales?.periodo,
    },
    {
      titleText: 'Fecha de Registro',
      dataIndex: 'fecha_registro',
      key: 'fecha_registro',
      width: 150,
    },
    // {
    //   titleText: 'Fecha Inicial',
    //   dataIndex: 'fecha_inicial',
    //   key: 'fecha_inicial',
    //   width: 150,
    // },
    // {
    //   titleText: 'Fecha Final',
    //   dataIndex: 'fecha_final',
    //   key: 'fecha_final',
    //   width: 150,
    // },
    {
      titleText: 'Lugar de Trabajo y Actividad',
      dataIndex: 'lugar_trabajo_actividad',
      key: 'lugar_trabajo_actividad',
      width: 200,
    },
    {
      titleText: 'Días de Guardia',
      dataIndex: 'dias_guardia',
      key: 'dias_guardia',
      width: 150,
    },
    {
      titleText: 'No. de Días',
      dataIndex: 'numero_dias',
      key: 'numero_dias',
      width: 150,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estado',
      key: 'estado',
      width: 100,
      render: (_, record) => record?.estados_globales?.descripcion || '',
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!hideTable && (
          <Table
            cols={columns}
            data={data?.results || []}
            pagination={false}
            paginationCallback={fetchPaginated}
            count={data?.count || 0}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            currentPage={pageActual}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            customActions={[
              {
                onClick: () => setVisibleAutorizar(true),
                permission: { model: 'registrodiasguardia', action: 'autorizar' },
                icon: <CheckCircleOutlined />,
                text: 'Autorizar',
                disabledBySelection: true,
                disabled: selectedData?.estados_globales?.id !== ESTATUS_ACTIVO,
                className: 'success',
              },
              {
                permission: { model: 'registrodiasguardia', action: 'cancelar' },
                onClick: () => setVisibleCancelar(true),
                text: 'Cancelar',
                icon: <CheckCircleOutlined />,
                disabledBySelection: true,
                disabled: selectedData?.estados_globales?.id !== ESTATUS_AUTORIZADO,
                className: 'cancel',
              },
              {
                permission: { model: 'registrodiasguardia', action: 'reactivar' },
                onClick: () => setVisibleReactivar(true),
                text: 'Reactivar',
                icon: <RedoOutlined />,
                disabledBySelection: true,
                disabled: selectedData?.estados_globales?.id !== ESTATUS_CANCELADO,
                className: 'success',
              },
            ]}
            permission={permission}
            disableDelete={selectedData?.estados_globales?.id !== ESTATUS_ACTIVO}
            disableEdit={selectedData?.estados_globales?.id !== ESTATUS_ACTIVO}
            allowExportParam={false}
          />
        )}
        <Modal
          open={open}
          width="80%"
          bodyStyle={selectedRowKeys.length ? { height: '50vh', overflowY: 'auto' } : null}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${nameModel} `}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstErrorDatePicker
            name="general"
            disabled={disableFields}
          >
            <Row gutter={10}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="antiguedad_texto"
                  label="Antigüedad"
                  rules={rules.estatus}
                  initialValue={fatherData.antiguedad_texto}
                  hasFeedback
                >
                  <Input
                    allowClear
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name={guardiasVacacionalGeneralName}
                  label="Año de Guardias"
                  rules={rules.required}
                  initialValue={guardiasVacacionalGeneral[guardiasVacacionalGeneral.length - 1]?.id}
                  hasFeedback
                >
                  <Select
                    render={(record) => record?.periodo_fiscal?.periodo}
                    dataSource={guardiasVacacionalGeneral}
                    disabled={selectedRowKeys.length}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name={periodoVacacionalName}
                  label="Periodo Vacacional"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    render={(record) => record?.periodos_vacacionales?.periodo}
                    dataSource={PeriodosVacacionales}
                    disabled={selectedRowKeys.length}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="fecha_registro"
                  label="Fecha de Registro"
                  initialValue={moment()}
                  hasFeedback
                >
                  <DatePicker
                    allowClear
                    format="DD-MM-YYYY"
                    placeholder=""
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="lugar_trabajo_actividad"
                  label="Lugar de Trabajo y Actividad"
                  hasFeedback
                >
                  <Input
                    allowClear
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="fecha_inicial"
                  label="Fecha Inicial"
                  rules={rules.required}
                  hasFeedback
                >
                  <DatePicker
                    allowClear
                    format="DD-MM-YYYY"
                    placeholder=""
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="fecha_final"
                  label="Fecha Final"
                  rules={rules.required}
                  hasFeedback
                >
                  <DatePicker
                    allowClear
                    format="DD-MM-YYYY"
                    placeholder=""
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="dias_guardia"
                  label="Días de Guardías"
                  hasFeedback
                >
                  <Input
                    disabled
                  />
                </Form.Item>
              </Col>
              {!!selectedRowKeys.length && (
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name={workingDaysName}
                    label="¿Acepta Sábado y Domingo?"
                    rules={rules.required}
                    hasFeedback
                    initialValue
                  >
                    <Select
                      trueFalse
                      disabled={disableFields}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name={aplicaGuardias}
                  label="¿Requiere Modificar Jornada Laboral?"
                  rules={rules.required}
                  hasFeedback
                  initialValue={false}
                >
                  <Select
                    trueFalse
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const tipo = getFieldValue(aplicaGuardias);
                  return tipo === true && (
                    <>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          name="jornada_laboral"
                          label="Jornada Laboral Actual"
                          hasFeedback
                          initialValue={fatherData.jornada_laboral}
                        >
                          <Input
                            disabled
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          name="de_las"
                          label="Hora Inicial"
                          rules={rules.required}
                          hasFeedback
                          initialValue={fatherData?.de_las}
                        >
                          <TimePicker
                            format="hh:mm A"
                            use24Hours
                            style={{ width: '100%' }}
                            disabled={disableFields}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          name="a_las"
                          label="Hora Final"
                          rules={rules.required}
                          hasFeedback
                          initialValue={fatherData?.a_las}
                        >
                          <TimePicker
                            format="hh:mm A"
                            use24Hours
                            style={{ width: '100%' }}
                            disabled={disableFields}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  );
                }}
              </Form.Item>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  hasFeedback
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="empleado"
              label="Empleado"
              hasFeedback
              hidden
              initialValue={fatherData.id}
            >
              <Input
                hidden
              />
            </Form.Item>
            <Form.Item
              name="antiguedad_numerico_anos"
              label="Antiguedad Numerico Años"
              hasFeedback
              hidden
              initialValue={fatherData.antiguedad_numerico_anos}
            >
              <InputNumber
                hidden
              />
            </Form.Item>

            <Form.Item
              name="antiguedad_numerico_dias"
              label="Antiguedad Numerico Dias"
              hasFeedback
              hidden
              initialValue={fatherData.antiguedad_numerico_dias}
            >
              <InputNumber
                hidden
              />
            </Form.Item>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>

          {!!selectedRowKeys.length && (
            <RegistroGuardiasFechas
              formParent={form}
              parentId={selectedRowKeys[0]}
              permission={getModelPermission('registrodiasguardiafechas')}
              dates={dates}
              disableFields={disableFields}
            />
          )}
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`${nameModelSingular} ${registroName}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar ${nameModelSingular} ${registroName}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          ConfirmAutorize
          onCancel={() => setVisibleCancelar(false)}
          contentTitle="Cancelar Guardias" // ${form.getFieldValue('periodo_detalle') || ''}`}
          open={visibleCancelar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle={`Reactivar ${nameModelSingular} ${registroName}`}
          open={visibleReactivar}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default RegistroGuardiasTable;
